import React, { useEffect, useState } from 'react';
import { KeyRoundIcon, ShieldCheckIcon } from 'lucide-react';
import { Modal } from '../ui/Modal';
import { Button } from '../ui/Button';
import { PinInput } from './PinInput';
import { useNotes } from '../../contexts/NotesContext';
import { attemptsLeft, cooldownRemaining, maskEmail, pinProblem } from '../../utils/notesLock';
import { account } from '../../data/account';
type Mode = 'create' | 'change' | 'remove';

/**
 * Sets, changes or removes the Notes PIN. Changing or removing requires the
 * current PIN — forgetting it is handled by the emailed reset, not here.
 */
export function PinSetupDialog({
  open,
  mode,
  onClose,
  onForgot
}: {
  open: boolean;
  mode: Mode;
  onClose: () => void;
  onForgot: () => void;
}) {
  const {
    lockCredential,
    createPin,
    changePin,
    clearPin
  } = useNotes();
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    if (open) {
      setCurrent('');
      setNext('');
      setConfirm('');
      setError(null);
    }
  }, [open, mode]);
  const cooldown = cooldownRemaining(lockCredential);
  const left = attemptsLeft(lockCredential);
  function submit(event: React.FormEvent) {
    event.preventDefault();
    setError(null);
    if (mode === 'remove') {
      if (!clearPin(current)) {
        setError('That PIN is not correct.');
        return;
      }
      onClose();
      return;
    }
    const problem = pinProblem(next);
    if (problem) {
      setError(problem);
      return;
    }
    if (next !== confirm) {
      setError('The two PINs do not match.');
      return;
    }
    if (mode === 'create') {
      createPin(next);
      onClose();
      return;
    }
    if (!changePin(current, next)) {
      setError('Your current PIN is not correct.');
      return;
    }
    onClose();
  }
  const title = mode === 'create' ? 'Set your Notes PIN' : mode === 'change' ? 'Change your Notes PIN' : 'Remove your Notes PIN';
  return <Modal open={open} onClose={onClose} title={title} description={mode === 'create' ? 'One PIN protects every note you choose to lock. Campuzon stores only a hashed version of it, never the digits.' : mode === 'change' ? 'Prove you know the current PIN, then choose a new one.' : 'Removing the PIN unlocks every locked note on this account.'} footer={<>
          <Button variant="ghost" onClick={onClose} data-id="element-3796" data-mp="1nbyl1gasspie">
            Cancel
          </Button>
          <Button variant={mode === 'remove' ? 'danger' : 'primary'} onClick={submit} disabled={cooldown > 0} data-id="element-3797" data-mp="228zdxn4q6qig">
            {mode === 'create' ? 'Set PIN' : mode === 'change' ? 'Change PIN' : 'Remove PIN'}
          </Button>
        </>} data-id="element-3795" data-mp="19j0kzgneu98i">
      <form onSubmit={submit} className="space-y-3.5" data-id="element-3798" data-mp="2xoldaer75oq">
        {mode !== 'create' && <div data-id="element-3799" data-mp="2ejqgvjcxdnig">
            <label className="mb-1.5 block text-[13px] font-medium text-ink" data-id="element-3800" data-mp="2elb9h3foiwdo">Current PIN</label>
            <PinInput value={current} onChange={setCurrent} label="Current PIN" autoFocus invalid={error !== null} disabled={cooldown > 0} data-id="element-3801" data-mp="2jibdz9wvrr2o" />
            <div className="mt-1.5 flex flex-wrap items-center gap-2" data-id="element-3802" data-mp="29rgrq5jmwrh8">
              <button type="button" onClick={onForgot} className="text-[12px] font-medium text-brand-800 hover:underline" data-id="element-3803" data-mp="1vsza8pkbob7r">
                I have forgotten my PIN
              </button>
              {left < 5 && cooldown === 0 && <span className="text-[12px] text-warn-700" data-id="element-3804" data-mp="1qvgwmlvd17tf">
                  {left} attempt{left === 1 ? '' : 's'} left before a short wait.
                </span>}
            </div>
          </div>}

        {mode !== 'remove' && <>
            <div data-id="element-3805" data-mp="1l6z42jwzwtgh">
              <label className="mb-1.5 block text-[13px] font-medium text-ink" data-id="element-3806" data-mp="1l5eaoxmf3gk7">
                {mode === 'create' ? 'New PIN' : 'New PIN'}
              </label>
              <PinInput value={next} onChange={setNext} label="New PIN" autoFocus={mode === 'create'} invalid={error !== null} data-id="element-3807" data-mp="1c5r9ocxlocro" />
              <p className="mt-1.5 text-[12px] text-ink-muted" data-id="element-3808" data-mp="127uxdhsxfbrz">
                4 to 6 digits. Not all the same digit, and not in sequence.
              </p>
            </div>
            <div data-id="element-3809" data-mp="shrxqyrauzko">
              <label className="mb-1.5 block text-[13px] font-medium text-ink" data-id="element-3810" data-mp="sh9yrud96792">Confirm PIN</label>
              <PinInput value={confirm} onChange={setConfirm} label="Confirm PIN" invalid={error !== null} data-id="element-3811" data-mp="ogq2su4f19oi" />
            </div>
          </>}

        {cooldown > 0 && <p className="rounded-lg border border-warn-100 bg-warn-50 px-3.5 py-2.5 text-[12px] leading-5 text-ink" data-id="element-3812" data-mp="ei0jqy0s9j6o">
            Too many incorrect attempts. Try again in {cooldown} second{cooldown === 1 ? '' : 's'}, or
            reset your PIN by email.
          </p>}

        {error && <p className="rounded-lg border border-danger-100 bg-danger-50 px-3.5 py-2.5 text-[12px] leading-5 text-danger-700" data-id="element-3813" data-mp="303juaf03jr91">
            {error}
          </p>}

        <div className="flex items-start gap-2 rounded-lg border border-line bg-canvas px-3.5 py-3" data-id="element-3814" data-mp="343139ieqf6iy">
          {mode === 'create' ? <ShieldCheckIcon className="mt-0.5 h-4 w-4 shrink-0 text-ink-subtle" data-id="element-3815" data-mp="2cdpim0rik8qd" /> : <KeyRoundIcon className="mt-0.5 h-4 w-4 shrink-0 text-ink-subtle" data-id="element-3816" data-mp="2gfsv8spx5jd8" />}
          <p className="text-[12px] leading-5 text-ink-muted" data-id="element-3817" data-mp="2l60yisp04ds3">
            {mode === 'remove' ? 'Your notes stay private to you either way — the PIN protects them from someone holding your unlocked device.' : `If you forget it, we send a code to ${maskEmail(account.email)} to verify the account before letting you choose a new one. The PIN itself cannot be recovered or shown to you.`}
          </p>
        </div>
      </form>
    </Modal>;
}